import React, { useEffect, useState } from 'react';
import { AlarmClock, Siren } from 'lucide-react';
import { CLASSE_NIVEL, situacaoDoPrazo, type SolicitacaoParaPrazo } from '../../src/lib/prazoSolicitacaoVisual';

/** Relógio compartilhado: um timer só para todos os selos da tela, de minuto em minuto. */
let agoraAtual = new Date();
const ouvintes = new Set<(d: Date) => void>();
let timer: ReturnType<typeof setInterval> | null = null;

export function useAgora(): Date {
  const [agora, setAgora] = useState(agoraAtual);
  useEffect(() => {
    ouvintes.add(setAgora);
    if (!timer) {
      timer = setInterval(() => {
        agoraAtual = new Date();
        ouvintes.forEach(f => f(agoraAtual));
      }, 60_000);
    }
    return () => {
      ouvintes.delete(setAgora);
      if (!ouvintes.size && timer) { clearInterval(timer); timer = null; }
    };
  }, []);
  return agora;
}

/** Selo "Faltam 10h" / "Atrasada há 2h" / "Entregue no prazo" (#6356). Sem prazo: nada. */
export function PrazoBadge({ tarefa, className = '' }: { tarefa: SolicitacaoParaPrazo; className?: string }) {
  const agora = useAgora();
  const s = situacaoDoPrazo(tarefa, agora);
  if (s.nivel === 'sem_prazo') return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold whitespace-nowrap normal-case tracking-normal ${CLASSE_NIVEL[s.nivel]} ${className}`}
      title={tarefa.vencimento ? `Prazo: ${new Date(`${tarefa.vencimento.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR')} às 18h` : undefined}
    >
      <AlarmClock size={11} aria-hidden="true" />
      {s.texto}
    </span>
  );
}

/** Urgência ainda aberta? É o que ganha o destaque visual (#6357). */
export const ehUrgenciaAberta = (t: { prioridade?: string | null; solicitacao_status?: string | null }) =>
  t.prioridade === 'urgencia' && t.solicitacao_status !== 'concluido' && t.solicitacao_status !== 'rejeitado';

/** Classes do cartão/linha de urgência aberta: faixa vermelha à esquerda e fundo rosado. */
// No escuro o rosado precisa de um vermelho mais aceso que o 950: sobre o cinza do quadro ele deixava o
// cartão urgente mais apagado que os vizinhos. O hover mantém o tom (sem ele a linha voltava a cinza).
export const CLASSE_URGENCIA = 'border-l-4 !border-l-red-600 bg-red-50/60 dark:bg-red-900/25 hover:bg-red-50 dark:hover:bg-red-900/35';

export function SeloUrgencia() {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-black uppercase whitespace-nowrap shrink-0 bg-red-600 text-white" title="Solicitação urgente">
      <Siren size={11} aria-hidden="true" /> Urgente
    </span>
  );
}
